import type { ActiveProcessesItem } from 'framework/ViewTypes/embeddable';
import {
  getCandidateCheckStates,
  getCandidateChecks,
  isEveryCheckPassed,
  resolveCheckText,
} from '../../shared/candidateChecks';
import type { ApplicationDetails, ApplicationStageActions } from '../../shared/types';
import { getCalculatePercentileAction, getEditApplicationAction, getRejectApplicationAction } from './shared';
import type { CreateApplicationStageConfig } from './types';

const getCheckProcesses = (application: ApplicationDetails, actions: ApplicationStageActions): ActiveProcessesItem[] =>
  getCandidateCheckStates(application).flatMap(({ job, state }) =>
    state.action
      ? [
          {
            id: job.id,
            title: job.processTitle,
            description: state.action.description && resolveCheckText(state.action.description, application),
            action: {
              id: state.action.id,
              label: state.action.label,
              variant: 'contained' as const,
              onClick: state.action.onClick(actions),
            },
          },
        ]
      : [],
  );

const getQualifyCandidateProcess = (
  { candidateQualification }: ApplicationDetails,
  actions: ApplicationStageActions,
): ActiveProcessesItem => ({
  id: 'qualify-candidate',
  title: 'Qualify candidate',
  action:
    candidateQualification === 'initiated'
      ? {
          id: 'make-decision-on-candidate',
          label: 'Make decision on candidate',
          variant: 'contained',
          onClick: actions.makeDecisionOnCandidate,
        }
      : {
          id: 'start-candidate-qualification',
          label: 'Start candidate qualification',
          variant: 'contained',
          onClick: actions.startCandidateQualification,
        },
});

const getEvaluatingProcesses = (
  application: ApplicationDetails,
  actions: ApplicationStageActions,
): ActiveProcessesItem[] => {
  const checkProcesses = getCheckProcesses(application, actions);

  if (checkProcesses.length) return checkProcesses;

  return isEveryCheckPassed(application) ? [getQualifyCandidateProcess(application, actions)] : [];
};

export const createEvaluatingStageConfig: CreateApplicationStageConfig = actions => ({
  badge: { id: 'evaluating', label: 'Evaluating', color: 'info' },
  getAttributes: () => [],
  getActions: () => [
    getRejectApplicationAction(actions),
    getEditApplicationAction(actions),
    getCalculatePercentileAction(actions),
  ],
  getActiveProcessesProps: application => ({
    collapsible: false,
    items: getEvaluatingProcesses(application, actions),
  }),
  getChecks: getCandidateChecks,
});
